import { Check } from 'lucide-react'
import { Modal } from '@/components/Modal'
import { TokenIcon } from '@/components/icons/TokenIcons'
import { ChainIcon } from '@/components/ChainIcon'
import { useThemeColors } from '@/hooks/useThemeColors'
import {
  STABLECOIN_PICKER_LIST,
  NATIVE_NETWORK_OPTIONS,
  type TokenSelection,
} from '@/config/tokens'
import { FONT_BODY, FONT_HEADING } from '@/config/theme'
import type { Chain } from '@/types'

interface TokenSelectorModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (selection: TokenSelection) => void
  /** Currently-selected canonical token symbol (for the selected check). */
  selectedToken: string
  /** Currently-selected chain — disambiguates which network-first native row is active. */
  selectedChain?: Chain | null
}

const SECTION_HEADER_STYLE: React.CSSProperties = {
  padding: '8px 20px',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  fontFamily: FONT_HEADING,
}

export function TokenSelectorModal({
  isOpen,
  onClose,
  onSelect,
  selectedToken,
  selectedChain,
}: TokenSelectorModalProps) {
  const c = useThemeColors()

  const handleSelect = (selection: TokenSelection) => {
    onSelect(selection)
    onClose()
  }

  const rowStyle: React.CSSProperties = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 20px',
    background: 'transparent',
    border: 'none',
    borderBottom: `1px solid ${c.dv}`,
    cursor: 'pointer',
    transition: 'background 0.15s',
    fontFamily: FONT_BODY,
  }
  const onEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    ;(e.currentTarget as HTMLButtonElement).style.background = c.blB
  }
  const onLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    ;(e.currentTarget as HTMLButtonElement).style.background = 'transparent'
  }

  return (
    <Modal show={isOpen} onClose={onClose} title="Select token">
      <div>
        {/* Stablecoins — network-agnostic, chosen by symbol (chain picked separately). */}
        <div>
          <div style={{ ...SECTION_HEADER_STYLE, background: c.iB, color: c.mu }}>Stablecoins</div>
          {STABLECOIN_PICKER_LIST.map((token) => {
            const isSelected = token.symbol === selectedToken
            return (
              <button
                key={token.symbol}
                onClick={() => handleSelect({ symbol: token.symbol })}
                style={rowStyle}
                onMouseEnter={onEnter}
                onMouseLeave={onLeave}
              >
                <TokenIcon token={{ symbol: token.symbol, color: token.color }} size={36} />
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: c.tx }}>{token.symbol}</div>
                  <div style={{ fontSize: 13, color: c.mu, marginTop: 1 }}>{token.name}</div>
                </div>
                {isSelected && <Check size={17} color={c.gr} />}
              </button>
            )
          })}
        </div>

        {/* Native tokens — NETWORK-FIRST (#254): one row per network so ETH-on-Base/Arbitrum/
            Optimism and Robinhood are directly pickable. Row label is the network; the native
            symbol is the subtitle. Selecting sets token + that exact chain. */}
        <div>
          <div style={{ ...SECTION_HEADER_STYLE, background: c.iB, color: c.mu }}>Native tokens</div>
          {NATIVE_NETWORK_OPTIONS.map((opt) => {
            const isSelected = opt.symbol === selectedToken && opt.chain === selectedChain
            return (
              <button
                key={opt.chain}
                onClick={() => handleSelect({ symbol: opt.symbol, chain: opt.chain })}
                style={rowStyle}
                onMouseEnter={onEnter}
                onMouseLeave={onLeave}
              >
                <ChainIcon chain={opt.chain} size={36} />
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: c.tx }}>{opt.networkName}</div>
                  <div style={{ fontSize: 13, color: c.mu, marginTop: 1 }}>{opt.symbol}</div>
                </div>
                {isSelected && <Check size={17} color={c.gr} />}
              </button>
            )
          })}
        </div>
      </div>
    </Modal>
  )
}
